'use client';

import Script from 'next/script';
import crypto from 'crypto';
import React, { useEffect, useState } from 'react';
import { getCookieWckDisplayMktSID } from '@wconcept/wconcept-common/utils/cookie';
import { getMolocoCustNo } from '@utils/domainUtil';
import { byteArrayHexa, getAge, getAgeGroup } from '@utils/commonUtils';
import { putTagManagerWithDataLayer } from '@utils/tagManagerUtil';
import TagManager from 'react-gtm-module';
import { usePathname } from 'next/navigation';
import { BrazeManager } from '@utils/brazeManager';
import {
  AppInfo,
  DecodeAssistData,
} from '@wconcept/wconcept-common/models/display';
import { initNaverAnalytics } from './initAnalytics';

const tagManagerArgs = {
  gtmId: process.env.NEXT_PUBLIC_GTM_ID as string,
};

export const GRADE_NAME: { [key: string]: string } = {
  '1': 'VVIP',
  '2': 'VIP',
  '3': 'PRIME',
  '4': 'BEST',
  '5': 'FRIEND',
  '6': 'WPEOPLE',
  '7': 'LOGIN',
  '8': 'GUEST',
};

function MarkeingScriptTags({
  assistData,
  isLogin,
  appInfo,
}: {
  assistData: DecodeAssistData;
  isLogin: boolean;
  appInfo: AppInfo;
}) {
  const pathname = usePathname();
  const [isCdnLoaded, setIsCdnLoaded] = useState(false);

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    if (!['/rn/women'].includes(pathname)) {
      window.AF && window.AF('banners', 'hideBanner');
    }
  }, [pathname]);

  useEffect(() => {
    const init = async () => {
      if (assistData?.user) {
        // brazeInit();
        let GAGender = ''; // M F
        const GAcustomerdegree =
          GRADE_NAME[assistData?.user?.gender.replace(/(^0+)/, '')];
        let GACustAge = 0;
        const key = [
          0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a,
          0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a,
          0x0a, 0x0a,
        ];
        const iv = [
          0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a,
          0x0a, 0x0a, 0x0a, 0x0a, 0x0a,
        ];
        const keyHex = byteArrayHexa(key);
        const ivHex = byteArrayHexa(iv);

        // Convert to Buffer objects for crypto
        const keyBuffer = Buffer.from(keyHex, 'hex');
        const ivBuffer = Buffer.from(ivHex, 'hex');

        // Using Node.js crypto instead of FrontCryptoJS
        const cipher = crypto.createCipheriv(
          'aes-192-cbc',
          keyBuffer as unknown as crypto.CipherKey,
          ivBuffer as unknown as crypto.BinaryLike
        );
        let encrypted = cipher.update(
          assistData?.user?.userNo,
          'utf8',
          'base64'
        );
        encrypted += cipher.final('base64');
        const base64UserNo = encrypted;

        let GACustEncryp = '';
        const gadevicetype = appInfo.isNativeAppBoth ? 'APP' : 'Mobile';
        const GAIsLogin = isLogin ? 'O' : 'X';
        if (isLogin) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          GACustAge = getAge(assistData?.user?.birth);
          GAGender = assistData?.user?.gender;
          GACustEncryp = base64UserNo; // as-is 기준 gtm dataLayer userNo 암호화
        }
        putTagManagerWithDataLayer(appInfo.isNativeAppBoth, {
          w_gender: GAGender,
          w_age: GACustAge,
          uid: GACustEncryp, // 암호화
          w_cust_key: GACustEncryp,
          w_member_type: GAcustomerdegree,
          login_OX: GAIsLogin,
          pageGroup1: 'MAIN',
          pageGroup2: '',
          pageGroup3: '',
          deviceType: gadevicetype,
          molocoCustNo: getMolocoCustNo(false),
          wckDisplayMktSID: await getCookieWckDisplayMktSID(),
        });
      }
    };
    init();
  }, [appInfo.isNativeAppBoth, assistData.user, isLogin]);

  useEffect(() => {
    BrazeManager.initialize(process.env.NEXT_PUBLIC_BRAZE_KEY || '', {
      baseUrl: 'https://sdk.iad-03.braze.com/api/v3',
      enableLogging: process.env.NEXT_PUBLIC_MODE !== 'prod',
      serviceWorkerLocation: `/serviceworker.js`,
      allowUserSuppliedJavascript: true,
    });

    window.braze.requestPushPermission();
  }, []);

  useEffect(() => {
    if (!assistData.isLogin || !assistData.user?.userNo) return;

    // 사용자 식별
    if ('changeUser' in window.braze) {
      (window.braze as any).changeUser(assistData.user.userNo);
    }

    // 사용자 속성 설정
    if (assistData.user.grade) {
      const getPreGrade = () => {
        const currentGrade = Number(assistData.user.grade);
        if (Number.isNaN(currentGrade)) return '1';
        const preGrade = currentGrade - 1;
        return preGrade > 1 ? preGrade.toString() : '1';
      };

      window?.braze
        ?.getUser()
        ?.setCustomUserAttribute('custPreGrade', getPreGrade());
    }
  }, [assistData.isLogin, assistData.user?.userNo, assistData.user?.grade]);

  return (
    <>
      <Script
        id="braze"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          +function(a,p,P,b,y){a.braze={};a.brazeQueue=[];for(var s="BrazeSdkMetadata DeviceProperties Card Card.prototype.dismissCard Card.prototype.removeAllSubscriptions Card.prototype.removeSubscription Card.prototype.subscribeToClickedEvent Card.prototype.subscribeToDismissedEvent Card.fromContentCardsJson Banner CaptionedImage ClassicCard ControlCard ContentCards ContentCards.prototype.getUnviewedCardCount Feed Feed.prototype.getUnreadCardCount ControlMessage InAppMessage InAppMessage.SlideFrom InAppMessage.ClickAction InAppMessage.DismissType InAppMessage.OpenTarget InAppMessage.ImageStyle InAppMessage.Orientation InAppMessage.TextAlignment InAppMessage.CropType InAppMessage.prototype.closeMessage InAppMessage.prototype.removeAllSubscriptions InAppMessage.prototype.removeSubscription InAppMessage.prototype.subscribeToClickedEvent InAppMessage.prototype.subscribeToDismissedEvent InAppMessage.fromJson FullScreenMessage ModalMessage HtmlMessage SlideUpMessage User User.Genders User.NotificationSubscriptionTypes User.prototype.addAlias User.prototype.addToCustomAttributeArray User.prototype.addToSubscriptionGroup User.prototype.getUserId User.prototype.incrementCustomUserAttribute User.prototype.removeFromCustomAttributeArray User.prototype.removeFromSubscriptionGroup User.prototype.setCountry User.prototype.setCustomLocationAttribute User.prototype.setCustomUserAttribute User.prototype.setDateOfBirth User.prototype.setEmail User.prototype.setEmailNotificationSubscriptionType User.prototype.setFirstName User.prototype.setGender User.prototype.setHomeCity User.prototype.setLanguage User.prototype.setLastKnownLocation User.prototype.setLastName User.prototype.setPhoneNumber User.prototype.setPushNotificationSubscriptionType InAppMessageButton InAppMessageButton.prototype.removeAllSubscriptions InAppMessageButton.prototype.removeSubscription InAppMessageButton.prototype.subscribeToClickedEvent automaticallyShowInAppMessages destroyFeed hideContentCards showContentCards showFeed showInAppMessage toggleContentCards toggleFeed changeUser destroy getDeviceId initialize isPushBlocked isPushPermissionGranted isPushSupported logCardClick logCardDismissal logCardImpressions logContentCardImpressions logContentCardsDisplayed logCustomEvent logFeedDisplayed logInAppMessageButtonClick logInAppMessageClick logInAppMessageHtmlClick logInAppMessageImpression logPurchase openSession requestPushPermission removeAllSubscriptions removeSubscription requestContentCardsRefresh requestFeedRefresh requestImmediateDataFlush enableSDK isDisabled setLogger setSdkAuthenticationSignature addSdkMetadata disableSDK subscribeToContentCardsUpdates subscribeToFeedUpdates subscribeToInAppMessage subscribeToSdkAuthenticationFailures toggleLogging unregisterPush wipeData handleBrazeAction".split(" "),i=0;i<s.length;i++){for(var m=s[i],k=a.braze,l=m.split("."),j=0;j<l.length-1;j++)k=k[l[j]];k[l[j]]=(new Function("return function "+m.replace(/\\./g,"_")+"(){window.brazeQueue.push(arguments); return true}"))()}window.braze.getCachedContentCards=function(){return new window.braze.ContentCards};window.braze.getCachedFeed=function(){return new window.braze.Feed};window.braze.getUser=function(){return new window.braze.User};(y=p.createElement(P)).type='text/javascript';
            y.src='https://js.appboycdn.com/web-sdk/6.3/braze.min.js';
            y.async=1;(b=p.getElementsByTagName(P)[0]).parentNode.insertBefore(y,b)
          }(window,document,'script');
        `,
        }}
      />
      {/* <Script
        id="custNo"
        dangerouslySetInnerHTML={{
          __html: `
            window.deviceName = '${isMobile ? 'Mobile' : 'PC'}';
            window.isDesktop = ${!!isDesktop};
            window.custNo = '${assistData?.user.userNo}';
          `,
        }}
      /> */}
      {/* 카카오비즈보드 PV 트래킹 */}
      <Script
        id="kakaoPixelCdnScriptTag"
        type="text/javascript"
        charSet="UTF-8"
        src="https://t1.daumcdn.net/adfit/static/kp.js"
        strategy="lazyOnload"
        onLoad={() => {
          try {
            window.kakaoPixel &&
              window.kakaoPixel('4785664593446063564')?.pageView();
          } catch (e) {
            console.error(e);
          }
        }}
      />
      {/* End 카카오비즈보드 PV 트래킹 */}

      {/* Google Ads */}
      <Script src="https://www.googletagmanager.com/gtag/js?id=AW-856703668" />
      <Script
        id="googleAdsScriptTag"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() { dataLayer.push(arguments); }
            gtag('js', new Date());
            gtag('config', 'AW-856703668');
          `,
        }}
      />
      {/* Google Tag Manager react-gtm-module 모듈로 관리중 */}
      {/* 페이스북 */}
      <Script
        async
        id="fbScriptTag"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            try {
              !function (f, b, e, v, n, t, s) { if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) }; if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []; t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s) }(window, document, 'script', '//connect.facebook.net/en_US/fbevents.js');
              fbq('init', '602779483174145');
              fbq('track', "PageView");
            } catch (e) {}
          `,
        }}
      />
      <Script
        id="fbEvents"
        src="https://connect.facebook.net/en_US/fbevents.js"
        strategy="afterInteractive"
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=""
          height="1"
          width="1"
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=602779483174145&ev=PageView&noscript=1"
        />
      </noscript>

      {/* <!--마케팅로그:Footer:타겟팅게이츠//--> */}
      {/* <!-- WIDERPLANET  SCRIPT START 2019.6.18 --> */}
      {/* ty 값이 페이지마다 달라야될꺼같은데 as-is도 Home/Join/Item/PurchaseComplete/Item 만 존재 */}
      <div id="wp_tg_cts" style={{ display: 'none' }} />
      <Script
        id="wptgTagscriptScriptTag"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
          var wptg_tagscript_vars = wptg_tagscript_vars || [];
          wptg_tagscript_vars.push(
          (function () {
            return {
              wp_hcuid: '${
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                isLogin
                  ? crypto
                      .createHash('md5')
                      .update(assistData?.user?.userNo || '')
                      .digest('hex')
                  : ''
              }', /* 고객넘버 등 Unique ID (ex. 로그인  ID, 고객넘버 등 )를 암호화하여 대입.*/
              ti: "20289", /* 광고주 코드 */
              ty: "Home", /* 트래킹태그 타입 */
              device: "mobile" /* 디바이스 종류  (web 또는  mobile)*/
            };
          }));
      `,
        }}
      />
      <Script
        id="widerplanet"
        strategy="lazyOnload"
        src="https://cdn-aitg.widerplanet.com/js/wp_astg_4.0.js"
      />
      {/* <!--마케팅로그:Footer:네이버//--> */}
      {/* <!-- 공통 적용 스크립트 , 모든 페이지에 노출되도록 설치. 단 전환페이지 설정값보다 항상 하단에 위치해야함 --> */}
      <Script
        id="wcslog"
        strategy="lazyOnload"
        src="https://wcs.naver.net/wcslog.js"
        onLoad={initNaverAnalytics}
      />
      {/* <!--//마케팅로그:Footer:네이버--> */}
      {/* AppsFlyer */}
      {/* <Script
        strategy="lazyOnload"
        id="appsFlyerCdnScriptTag"
        src="https://static.wconcept.co.kr/mobile/js/wconcept.mobile.marketing.min.js"
        onLoad={() => {
          console.log('AppsFlyer CDN script loaded successfully');
          setIsCdnLoaded(true);
        }}
      /> */}

      {/* <!-- 마케팅로그:AppsFlyer--> */}
      {/* {isCdnLoaded && (
        <Script
          id="appsFlyerScriptTag"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            try {
              var WCKAppsFlyer = new WCKAppsFlyer();
              !function (t, e, n, s, a, c, i, o, p) { t.AppsFlyerSdkObject = a, t.AF = t.AF || function () { (t.AF.q = t.AF.q || []).push([Date.now()].concat(Array.prototype.slice.call(arguments))) }, t.AF.id = t.AF.id || i, t.AF.plugins = {}, o = e.createElement(n), p = e.getElementsByTagName(n)[0], o.async = 1, o.src = "https://websdk.appsflyer.com?" + (c.length > 0 ? "st=" + c.split(",").sort().join(",") + "&" : "") + (i.length > 0 ? "af_id=" + i : ""), p.parentNode.insertBefore(o, p) }(window, document, "script", 0, "AF", "pba,banners", { pba: {webAppId: "${
                process.env.NEXT_PUBLIC_APPS_FLYER_PBA_KEY
              }"}, banners: { key: "${
              process.env.NEXT_PUBLIC_APPS_FLYER_BANNER_KEY
            }" } })
              ${!appInfo?.isNativeAppBoth && `AF('banners', 'showBanner')`}
            } catch (e) {
              console.error(e);
            }
          `,
          }}
        />
      )} */}

      {/* <!-- 마케팅로그:airBridge--> */}
      <Script
        id="airBridgeScriptTag"
        crossOrigin="anonymous"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          try {
            (function(a_,i_,r_,_b,_r,_i,_d,_g,_e){if(!a_[_b]){var n=function(){var c=i_.createElement(r_);c.onerror=function(){h.queue.filter(function(a){return 0<=_d.indexOf(a[0])}).forEach(function(a){a=a[1];a=a[a.length-1];"function"===typeof a&&a("error occur when load airbridge")})};c.async=1;c.src=_r;"complete"===i_.readyState?i_.head.appendChild(c):a_.addEventListener("load",function k(){a_.removeEventListener("load",k);i_.head.appendChild(c)})},h={queue:[],get isSDKEnabled(){return!1}};_i.concat(_d).forEach(function(c){var a=c.split("."),k=a.pop();a.reduce(function(p,q){return p[q]=p[q]||{}},h)[k]=function(){h.queue.push([c,arguments])}});a_[_b]=h;"undefined"!==typeof i_.documentMode&&(_r=_r.replace(/^https:/,""));0<_g?(_b=new (a_.XDomainRequest||a_.XMLHttpRequest),_i=function(){},_b.open("GET",_r),_b.timeout=_g,_b.onload=function(){n()},_b.onerror=_i,_b.onprogress=_i,_b.ontimeout=_i,_b.send()):n()}})(window,document,"script","airbridge","https://static.airbridge.io/sdk/latest/airbridge.min.js","init startTracking stopTracking openBanner setBanner setDownload setDownloads openDeeplink setDeeplinks sendWeb setUserAgent setMobileAppData setUserID clearUserID setUserEmail clearUserEmail setUserPhone clearUserPhone setUserAttribute removeUserAttribute clearUserAttributes setUserAlias removeUserAlias clearUserAlias clearUser setUserId setUserAttributes addUserAlias setDeviceAlias removeDeviceAlias clearDeviceAlias setDeviceIFV setDeviceIFA setDeviceGAID events.send events.signIn events.signUp events.signOut events.purchased events.addedToCart events.productDetailsViewEvent events.homeViewEvent events.productListViewEvent events.searchResultViewEvent".split(" "),["events.wait","fetchResource","createTouchpoint","createTrackingLink"],0);
            window.airbridge.init({
              app: ${JSON.stringify(
                process.env.NEXT_PUBLIC_AIRBRIDGE_APP_NAME
              )},
              webToken: ${JSON.stringify(
                process.env.NEXT_PUBLIC_AIRBRIDGE_TOKEN
              )},
            });
          } catch (e) {
            console.error(e);
          }
        `,
        }}
      />

      <Script
        src="https://t1.kakaocdn.net/kakao_js_sdk/2.4.0/kakao.min.js"
        integrity="sha384-mXVrIX2T/Kszp6Z0aEWaA8Nm7J6/ZeWXbL8UpGRjKwWe56Srd/iyNmWMBhcItAjH"
        crossOrigin="anonymous"
        strategy="lazyOnload" // 또는 'beforeInteractive', 'afterInteractive'
        onLoad={() => {
          try {
            if (
              typeof window !== 'undefined' &&
              !window.Kakao.isInitialized()
            ) {
              window.Kakao.init(process.env.NEXT_PUBLIC_KAKAO_KEY || '');
            }
          } catch (e) {
            console.log(e);
          }
        }}
      />

      <Script
        id="netfunnelScriptTag"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w, d, s, uri, fn) {
            w[fn] = w[fn] || function() {
              var c = {};
              c.tenantApiUrl = arguments[0];
              c.tenantNFUrl = arguments[1];
              c.tenantLoaderPort = arguments[2];
              (w[fn].l = w[fn].l || []).push(c);
            };
            var o = d.createElement(s);
            var p = d.getElementsByTagName(s)[0];
            o.async = 1;
            o.charset = 'utf-8';
            o.src = uri;
            p.parentNode.insertBefore(o, p);
          })
          (window, document, 'script', 'https://agent-lib.stclab.com/agents/client/javascript/netfunnel-javascript-agent.js', 'nfTag');
          nfTag('https://nf-setting-bucket.stclab.com/sk-kim-1619.netfunnel/nf-setting.json', 'https://sk-kim-1619.netfunnel.stclab.com', '443');
        `,
        }}
      />
    </>
  );
}

export default MarkeingScriptTags;
